import type { ScoreExclusionReason, ScoreInput, WorkerScore } from '../index.js'
import { formatScore } from '../../core/index.js'
import { describeAnchors } from './anchorText.js'
import { describeCounted } from './countedText.js'

const EXCLUSION_WORDS: Record<ScoreExclusionReason, string> = {
  switched_off: 'Your facility does not measure this',
  no_weight: 'Your facility gives this no weight',
  no_data: 'Nothing measured yet',
}

/** A value at or above this is going well: a green bar. */
const STRONG_FROM = 85
/** A value at or above this, short of strong, has room to grow: a gold bar. Only below it is a bar coral. */
const FAIR_FROM = 60
/** The top of a behavior's value, shown beside it so a bare 0 is not read as a count of call-outs. */
const FULL_VALUE = 100
/** Points lost that round to nothing are not shown as a loss. */
const SHOWN_LOSS_FROM = 0.05
/** The shortest a counted bar is drawn, in percent of the track, so a value at or near 0 still shows its colour instead of an empty track. */
const MIN_BAR_WIDTH = 4

/** The bar's colour by how the behavior is going, so a good month reads green and coral is kept for what needs attention. */
export function barColorClass(value: number): string {
  if (value >= STRONG_FROM) return 'bg-green'
  return value >= FAIR_FROM ? 'bg-gold' : 'bg-coral'
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** How wide a counted bar is drawn: its value, kept inside the track and never thinner than the shortest bar. */
export function barWidthPercent(value: number): number {
  return Math.max(MIN_BAR_WIDTH, Math.min(FULL_VALUE, value))
}

/** What sits under a bar: the count behind the value, how much the behavior weighs, and the points it cost when it cost any. */
function describeBar(input: ScoreInput): string {
  const lost = input.pointsLost
  const parts = [
    describeCounted(input),
    `${input.weight}% of your score`,
    lost !== null && lost !== undefined && lost >= SHOWN_LOSS_FROM ? `${lost.toFixed(1)} points to gain` : null,
  ]
  return parts.filter((part): part is string => part !== null).join(' · ')
}

/**
 * A period's score for the worker's own app: the line saying what it was
 * computed from, then one bar per behavior - the value from 0 to 100,
 * coloured by how it is going, with the count, the weight and any points
 * to gain in plain words beneath, what earns full marks and what earns
 * none, and the coaching line under the row
 * that is costing the most. A behavior the engine left out says why in
 * words and is never shown as a loss. (Staff see the same numbers as a
 * table; the worker had that table for a day and it read as all
 * penalty, so the bars came back on 12 October 2026.)
 */
export function WorkerBreakdownBars({ score, inputs }: { score: WorkerScore | null; inputs: readonly ScoreInput[] }) {
  if (inputs.length === 0) {
    return <div className="text-[11px] text-ink-mid">Nothing measured yet. Your first reported shift starts every bar.</div>
  }
  return (
    <div>
      {score && score.score !== null && (
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[10.5px] text-ink-mid">
          <span>
            <span className="font-bold text-ink">{formatScore(score.score)}</span> this period from{' '}
            <span className="font-bold text-ink">
              {score.scheduledShifts} scheduled {score.scheduledShifts === 1 ? 'shift' : 'shifts'}
            </span>
            {score.workedShifts < score.scheduledShifts && <>, {score.workedShifts} worked</>}
          </span>
          {score.provisional && <span className="rounded border border-gold/60 bg-gold-lt px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink">Provisional</span>}
          <span>computed {formatDateTime(score.computedAt)}</span>
        </div>
      )}
      <ul className="flex flex-col gap-3">
        {inputs.map((input) => {
          const excluded = input.included === false
          const value = Math.round(input.value)
          const anchors = excluded ? null : describeAnchors(input)
          return (
            <li key={input.key} className={excluded ? 'opacity-70' : ''}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="text-[11px] font-semibold text-ink">{input.label}</span>
                <span className="text-[11px] font-bold text-ink whitespace-nowrap">
                  {excluded ? (
                    'Not counted'
                  ) : (
                    <>
                      {value}
                      <span className="font-normal text-ink-mid"> / {FULL_VALUE}</span>
                    </>
                  )}
                </span>
              </div>
              <div className="h-2 bg-marine/10 rounded-full overflow-hidden" role="img" aria-label={excluded ? `${input.label}: not counted` : `${input.label}: ${value} of ${FULL_VALUE}`}>
                {!excluded && <div className={`h-full rounded-full ${barColorClass(input.value)}`} style={{ width: `${barWidthPercent(input.value)}%` }} />}
              </div>
              <div className="mt-1 text-[10px] text-ink-mid leading-snug">
                {excluded ? (input.excludedReason ? EXCLUSION_WORDS[input.excludedReason] : 'Not included this month') : describeBar(input)}
              </div>
              {anchors && <div className="text-[10px] text-ink-mid leading-snug">{anchors}</div>}
              {!excluded && input.nudge && <div className="mt-1 text-[10.5px] font-semibold text-marine-dk leading-snug">{input.nudge}</div>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
